import React, { useRef, useState } from 'react'
import moment from 'moment'
import DiaryDatePicker from '../../../../../Diary/DiaryDatePicker'
import ShiftTimePicker from '../../../../../Diary/ShiftTimePicker'
import { hapticTick } from '../../../../../../_utils/haptics'
import { buildCaption, VOICES, suggestedTags } from '../../data/captions'
import AutoGrow from '../AutoGrow'
import { SparkIcon, CalendarIcon, CloseIcon, PlusIcon } from '../icons'

const Seg = ({ value, options, onChange, label }) => (
  <div className="sx-seg" role="radiogroup" aria-label={label}>
    {options.map(o => (
      <button key={o.id} type="button" role="radio" aria-checked={value === o.id} className={`sx-seg-btn${value === o.id ? ' is-on' : ''}`} onClick={() => { if (value !== o.id) { hapticTick(); onChange(o.id) } }}>
        {o.label}
      </button>
    ))}
  </div>
)

// When people are scrolling: before work, lunch, after work, the evening
export const BEST_TIMES = [
  { h: 8, m: 0, label: '8am', note: 'Before work' },
  { h: 12, m: 30, label: '12:30pm', note: 'Lunch' },
  { h: 18, m: 0, label: '6pm', note: 'After work' },
  { h: 20, m: 30, label: '8:30pm', note: 'Evening' },
]
const atTime = (day, h, m) => moment(day).hours(h).minutes(m).seconds(0).milliseconds(0)

// Hashtags as chips: tap a suggestion to add it, × to take one off
const Hashtags = ({ tags, suggestions, onChange }) => {
  const [draft, setDraft] = useState('')
  const add = (raw) => {
    const t = String(raw || '').trim().replace(/^#*/, '').replace(/[^A-Za-z0-9_]/g, '')
    if (!t) return
    const tag = `#${t}`
    if (!tags.some(x => x.toLowerCase() === tag.toLowerCase())) onChange([...tags, tag])
    setDraft('')
  }
  const left = suggestions.filter(s => !tags.some(t => t.toLowerCase() === s.toLowerCase()))
  return (
    <div className="sx-field">
      <span className="sx-label">Hashtags</span>
      <div className="sx-tags">
        {tags.map(t => (
          <span key={t} className="sx-tag">
            {t}
            <button type="button" onClick={() => { hapticTick(); onChange(tags.filter(x => x !== t)) }} aria-label={`Remove ${t}`}><CloseIcon size={12} /></button>
          </span>
        ))}
        <input
          className="sx-tag-input"
          value={draft}
          placeholder={tags.length ? 'Add another' : 'Add a hashtag'}
          aria-label="Add a hashtag"
          onChange={(e) => {
            const v = e.target.value
            if (/[\s,]$/.test(v)) add(v)
            else setDraft(v)
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); add(draft) }
            if (e.key === 'Backspace' && !draft && tags.length) onChange(tags.slice(0, -1))
          }}
          onBlur={() => add(draft)}
        />
      </div>
      {left.length > 0 && (
        <div className="sx-tag-suggest" aria-label="Suggested hashtags">
          {left.slice(0, 6).map(s => (
            <button key={s} type="button" className="sx-chip" onClick={() => { hapticTick(); onChange([...tags, s]) }}><PlusIcon size={12} />{s.slice(1)}</button>
          ))}
        </div>
      )}
      <span className="sx-hint">{tags.length > 5 ? 'That’s a lot. Three to five usually does better.' : 'Three to five is plenty. One for your town helps local people find you.'}</span>
    </div>
  )
}

const CaptionPanel = ({ post, caption, salonName, town, onUpdate }) => {
  const [dayOpen, setDayOpen] = useState(false)
  const [note, setNote] = useState('')
  const dayRef = useRef(null)
  const at = post.plannedAt ? moment(post.plannedAt) : null
  const posted = post.status === 'posted'

  const plan = (m) => {
    if (m && m.isBefore(moment())) { setNote('That time has passed. Pick a later one.'); return }
    setNote('')
    hapticTick()
    onUpdate(m
      ? { plannedAt: m.toISOString(), status: posted ? 'posted' : 'planned' }
      : { plannedAt: null, status: posted ? 'posted' : 'draft' }, 'plan')
  }
  const day = at || moment().add(moment().hours() >= 18 ? 1 : 0, 'day')
  const chosen = at ? `${at.hours()}:${at.minutes()}` : ''
  const setCaption = (patch, key) => onUpdate({ caption: { ...caption, ...patch, edited: true } }, key)

  return (
    <div className="sx-caption-panel">
      <section className="sx-group" aria-label="When to post">
        <h3 className="sx-group-title">When to post</h3>
        <div className="sx-field">
          <span className="sx-label">Day</span>
          <button type="button" ref={dayRef} className="sx-input sx-picker-btn" onClick={() => { hapticTick(); setDayOpen(true) }} aria-haspopup="dialog">
            <CalendarIcon size={17} />
            <span>{at ? at.calendar(null, { sameDay: '[Today], dddd D MMMM', nextDay: '[Tomorrow], dddd D MMMM', nextWeek: 'dddd D MMMM', sameElse: 'dddd D MMMM' }) : 'Pick a day'}</span>
          </button>
          <DiaryDatePicker
            open={dayOpen}
            selected={at ? at.toDate() : new Date()}
            anchorEl={dayRef.current}
            onChange={(d) => {
              setDayOpen(false)
              if (moment(d).isBefore(moment(), 'day')) { setNote('That day has been and gone. Pick today or later.'); return }
              const h = at ? at.hours() : 12
              const m = at ? at.minutes() : 30
              let next = atTime(d, h, m)
              // Today, with the time already gone: the next good time today
              if (next.isBefore(moment())) {
                const later = BEST_TIMES.map(t => atTime(d, t.h, t.m)).find(t => t.isAfter(moment()))
                next = later || moment().add(1, 'hour').startOf('hour')
              }
              plan(next)
            }}
            onClose={() => setDayOpen(false)}
          />
        </div>
        <div className="sx-field">
          <span className="sx-label">Time</span>
          <div className="sx-times" role="radiogroup" aria-label="Time">
            {BEST_TIMES.map(t => {
              const m = atTime(day, t.h, t.m)
              const gone = m.isBefore(moment())
              const on = chosen === `${t.h}:${t.m}`
              return (
                <button key={t.label} type="button" role="radio" aria-checked={on} disabled={gone} className={`sx-time${on ? ' is-on' : ''}`} onClick={() => plan(m)}>
                  <strong>{t.label}</strong>
                  <span>{t.note}</span>
                </button>
              )
            })}
            <span className="sx-time sx-time--other">
              <ShiftTimePicker
                label="Remind me at"
                placeholder="Other time"
                selected={at && !BEST_TIMES.some(t => chosen === `${t.h}:${t.m}`) ? at.toDate() : null}
                onChange={(t) => plan(atTime(day, t.getHours(), t.getMinutes()))}
              />
            </span>
          </div>
          <span className={`sx-hint${note ? ' is-warn' : ''}`} role={note ? 'alert' : undefined}>
            {note || (at ? `We’ll remind you ${at.calendar(null, { sameDay: '[today at] h:mma', nextDay: '[tomorrow at] h:mma', nextWeek: 'dddd [at] h:mma', sameElse: 'ddd D MMM [at] h:mma' })}. It shows on your calendar and in the diary, ready to share.` : 'Pick a day and it shows on your calendar and in the diary on the day, ready to share.')}
          </span>
          {at && !posted && <button type="button" className="sx-link sx-reset" onClick={() => plan(null)}>Don’t remind me</button>}
        </div>
      </section>

      <section className="sx-group" aria-label="Caption">
        <div className="sx-group-head">
          <h3 className="sx-group-title">Caption</h3>
          <span className="sx-bailey-tag"><SparkIcon size={13} />Written by Bailey</span>
        </div>
        <Seg label="Voice" value={caption.voice} onChange={(v) => onUpdate({ caption: buildCaption(post, { voice: v, salon: salonName, town }) }, 'voice')} options={VOICES} />
        <AutoGrow minRows={4} className="sx-input sx-caption" value={caption.text} aria-label="Caption" onChange={(v) => setCaption({ text: v }, 'caption-text')} />
        {caption.edited && (
          <button type="button" className="sx-link sx-reset" onClick={() => { hapticTick(); onUpdate({ caption: buildCaption(post, { voice: caption.voice, salon: salonName, town }) }, 'caption-reset') }}>Let Bailey write it again</button>
        )}
        <Hashtags
          tags={caption.hashtags || []}
          suggestions={suggestedTags(post, town)}
          onChange={(hashtags) => setCaption({ hashtags }, 'hashtags')}
        />
        <label className="sx-field">
          <span className="sx-label">Alt text</span>
          <AutoGrow minRows={2} value={caption.alt || ''} onChange={(v) => setCaption({ alt: v }, 'alt')} />
          <span className="sx-hint">Read aloud to people who can’t see the picture. Paste it in Instagram’s Advanced settings.</span>
        </label>
      </section>
    </div>
  )
}

export default CaptionPanel
